import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { LogOut, Trophy, Trash2, Loader2, Sun, Moon, Monitor, MoonStar, Trees, Sunset as SunsetIcon, Flower2, Check, Share2, Copy, Download, LayoutDashboard, Layout, Zap, Lock } from 'lucide-react';
import { QrCodeSheet } from '@/components/QrCodeSheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { GoogleSheetSyncService } from '@/services/GoogleSheetSyncService';
import { useUserSettings, UserFeatures } from '@/lib/user-settings-context';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Settings2 } from 'lucide-react';
import { UNIT_VALUE } from '@/lib/score';
import { ThemePreviewSection } from '@/components/ThemePreviewSection';

const themes = [
  { name: 'Light', value: 'light', icon: Sun, color: 'bg-slate-100 !text-slate-900' },
  { name: 'Dark', value: 'dark', icon: Moon, color: 'bg-slate-950 text-white' },
  { name: 'Midnight', value: 'midnight', icon: MoonStar, color: 'bg-blue-950 text-white' },
  { name: 'Forest', value: 'forest', icon: Trees, color: 'bg-emerald-950 text-white' },
  { name: 'Sunset', value: 'sunset', icon: SunsetIcon, color: 'bg-purple-950 text-white' },
  { name: 'Rose', value: 'rose', icon: Flower2, color: 'bg-rose-950 text-white' },
  { name: 'System', value: 'system', icon: Monitor, color: 'bg-slate-500 text-white' },
];

const layouts = [
  { name: 'Classic', value: 'classic', icon: LayoutDashboard, description: 'Traditional bottom navigation' },
  { name: 'Modern', value: 'modern', icon: Layout, description: 'Sidebar-focused productivity' },
  { name: 'Neon', value: 'neon', icon: Zap, description: 'Vibrant, high-energy interface' },
];

export default function SettingsPage() {
  const { user, signOut, updatePassword } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const { settings, updateSettings, loading: settingsLoading } = useUserSettings();
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [wiping, setWiping] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ variant: 'destructive', title: 'Passwords do not match' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ variant: 'destructive', title: 'Password must be at least 6 characters' });
      return;
    }

    setUpdatingPassword(true);
    const { error } = await updatePassword(newPassword);
    setUpdatingPassword(false);

    if (error) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    } else {
      toast({ title: 'Password updated successfully!' });
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleExportCSV = async () => {
    if (!user) return;
    setExporting(true);

    try {
      const [sessionsRes, expensesRes] = await Promise.all([
        supabase
          .from('sessions')
          .select(`
            id,
            date,
            status,
            session_players (
              player_id,
              buy_ins,
              cash_out,
              net_amount,
              players (name)
            )
          `)
          .eq('created_by', user.id)
          .order('date', { ascending: false }),
        supabase
          .from('expenses')
          .select(`
            id,
            session_id,
            amount,
            description,
            payer_id,
            payer:players!payer_id(name),
            participants:expense_participants(
              player_id,
              share,
              player:players(name)
            )
          `)
          .eq('created_by', user.id)
      ]);

      if (sessionsRes.error) throw sessionsRes.error;
      if (expensesRes.error) throw expensesRes.error;

      const sessions = sessionsRes.data || [];
      const expenses = expensesRes.data || [];

      const headers = ['Date', 'Status', 'Player', 'Entries', 'Results', 'Score', 'Exp Paid', 'Exp Share', 'Exp Balance', 'Total Net'];
      const rows = [];

      for (const s of sessions) {
        // Pre-calculate expenses for this session
        const sessionExpenses = expenses.filter(e => e.session_id === s.id);
        const playerExpenseMap: Record<string, { paid: number, share: number }> = {};
        
        sessionExpenses.forEach(exp => {
          if (!playerExpenseMap[exp.payer_id]) playerExpenseMap[exp.payer_id] = { paid: 0, share: 0 };
          playerExpenseMap[exp.payer_id].paid += Number(exp.amount);
          
          exp.participants.forEach((p: any) => {
            if (!playerExpenseMap[p.player_id]) playerExpenseMap[p.player_id] = { paid: 0, share: 0 };
            playerExpenseMap[p.player_id].share += Number(p.share);
          });
        });

        for (const sp of (s as any).session_players || []) {
          const expData = playerExpenseMap[sp.player_id] || { paid: 0, share: 0 };
          const expBalance = expData.paid - expData.share;
          const totalNet = (sp.net_amount || 0) + expBalance;

          rows.push([
            new Date(s.date).toLocaleDateString(),
            s.status,
            sp.players?.name || 'Unknown',
            sp.buy_ins * ((s as any).buy_in_unit ?? UNIT_VALUE),
            sp.cash_out || 0,
            sp.net_amount || 0,
            expData.paid,
            expData.share,
            expBalance,
            totalNet
          ]);
        }
      }

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `ssc_sessions_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({ title: 'CSV exported successfully!' });
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Export failed', description: err.message });
    } finally {
      setExporting(false);
    }
  };

  const handleWipe = async () => {
    if (!user || confirmText !== 'DELETE') return;
    setWiping(true);

    try {
      // Delete in order: expense_participants → expenses → session_players → sessions → settlements → players
      await supabase.from('expense_participants').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('expenses').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('session_players').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('settlements').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('sessions').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('players').delete().neq('id', '00000000-0000-0000-0000-000000000000');

      // Sync to Google Sheets
      await GoogleSheetSyncService.syncWipe();

      // Clear any local storage
      localStorage.clear();

      setShowWipeDialog(false);
      setConfirmText('');
      toast({ title: 'All data wiped successfully.' });
      navigate('/');
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error', description: err.message });
    } finally {
      setWiping(false);
    }
  };

  const featureList = [
    { key: 'recent_results', title: 'Recent Results', description: 'Show recent session results on the dashboard' },
    { key: 'whos_hot', title: "Who's Hot?", description: 'Show top performing players on the dashboard' },
    { key: 'global_podium', title: 'Global Podium', description: 'Show the overall podium in statistics' },
    { key: 'date_range_filters', title: 'Date-Range Filters', description: 'Enable filtering statistics by date range' },
    { key: 'head_to_head', title: 'Head-to-Head Comparisons', description: 'Enable player vs player comparisons' },
    { key: 'milestone_badges', title: 'Milestone Badges', description: 'Show player achievements and rank badges' },
    { key: 'profit_loss_trend', title: 'Profit/Loss Trend Graph', description: 'Show the P/L graph on player profiles' },
    { key: 'player_search_filter', title: 'Player Search & Filter', description: 'Enable searching and filtering the player list' },
    { key: 'session_calendar_search', title: 'Session Calendar Search', description: 'Enable searching sessions by date' },
    { key: 'enable_vision', title: 'Enable Image Upload/OCR', description: 'Allow uploading photos of score sheets for AI extraction' },
    { key: 'achievement_badges', title: 'Achievement Badges', description: 'Show earned milestone badges on player profiles (Hot Streak, Whale, etc.)' },
    { key: 'seasonal_leaderboard', title: 'Seasonal Leaderboard', description: 'Show a Season tab on Statistics filtered to the current calendar quarter' },
    { key: 'session_templates', title: 'Session Group Templates', description: 'Save and reuse named player groups when starting a new session' },
    { key: 'realtime_session', title: 'Real-Time Live Session', description: 'Auto-update the active session scoreboard without page refreshes' },
    { key: 'extra_juice', title: 'Extra Juice', description: 'Enable confetti, haptics, and satisfying click sounds' },
  ];

  return (
    <div className="py-6 space-y-6">
      <h1 className="text-xl font-bold tracking-tight">Settings</h1>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 space-y-1">
          <p className="text-sm text-muted-foreground">Signed in as</p>
          <p className="font-semibold text-sm">{user?.email}</p>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Public Profile</p>
          </div>
          <p className="text-sm text-muted-foreground">
            Share your scores and session history with others using this link.
          </p>
          <div className="flex gap-2">
            <Input
              readOnly
              value={`${window.location.origin}/public/${user?.id}`}
              className="bg-muted font-mono text-xs"
            />
            <Button
              size="icon"
              variant="secondary"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/public/${user?.id}`);
                toast({ title: "Link copied to clipboard!" });
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
            <QrCodeSheet url={`${window.location.origin}/public/${user?.id}`} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <ThemePreviewSection
            currentTheme={theme}
            onSelectTheme={(t) => {
              setTheme(t);
              updateSettings({ theme: t });
            }}
          />
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Interface Layout</p>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {layouts.map((l) => (
              <button
                key={l.value}
                onClick={() => {
                  updateSettings({ ui_variant: l.value as any })
                    .then(() => toast({ title: `Switched to ${l.name} layout` }))
                    .catch(() => toast({ variant: 'destructive', title: 'Failed to update layout' }));
                }}
                className={cn(
                  "flex items-center p-4 rounded-2xl border-2 transition-all gap-4 relative text-left",
                  settings.ui_variant === l.value 
                    ? "border-primary bg-primary/5 ring-1 ring-primary" 
                    : "border-border hover:border-muted-foreground/30 bg-card"
                )}
              >
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center shadow-sm",
                  settings.ui_variant === l.value ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                )}>
                  <l.icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <p className={cn(
                    "text-sm font-bold",
                    settings.ui_variant === l.value ? "text-primary" : "text-foreground"
                  )}>{l.name}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{l.description}</p>
                </div>
                {settings.ui_variant === l.value && (
                  <div className="absolute top-4 right-4 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-primary-foreground" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Settings2 className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Feature Settings</p>
          </div>
          <div className="space-y-4">
            {featureList.map((feature) => (
              <div key={feature.key} className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor={feature.key} className="text-sm font-medium">
                    {feature.title}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
                <Switch
                  id={feature.key}
                  checked={settings[feature.key as keyof UserFeatures]}
                  onCheckedChange={(checked) => {
                    updateSettings({ [feature.key]: checked })
                      .then(() => toast({ title: 'Setting updated' }))
                      .catch(() => {
                        toast({
                          variant: "destructive",
                          title: "Error",
                          description: "Failed to update setting. Please try again.",
                        });
                      });
                  }}
                  disabled={settingsLoading}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Trophy className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <p className="font-semibold text-sm">SSC Score</p>
            <p className="text-xs text-muted-foreground">v2.0.1</p>
          </div>
        </CardContent>
      </Card>

      <Button
        variant="destructive"
        className="w-full h-12 gap-2"
        onClick={signOut}
      >
        <LogOut className="h-4 w-4" />
        Sign Out
      </Button>

      {/* Account Security */}
      <div className="pt-4 border-t border-border space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Account Security</h2>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-primary" />
              <p className="text-sm font-medium">Change Password</p>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="At least 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Repeat new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={updatingPassword || !newPassword || !confirmPassword}
              >
                {updatingPassword ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Update Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Data Management */}
      <div className="pt-4 border-t border-border space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Data Management</h2>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Download all your session data in CSV format for use in Excel or other tools.
            </p>
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={handleExportCSV}
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Export to CSV
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Danger Zone */}
      <div className="pt-4 border-t border-border space-y-3">
        <h2 className="text-sm font-semibold text-destructive">Danger Zone</h2>
        <Card className="border-destructive/30">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm text-muted-foreground">
              Permanently delete all sessions, players, and history. This cannot be undone.
            </p>
            <Button
              variant="destructive"
              className="w-full gap-2"
              onClick={() => setShowWipeDialog(true)}
            >
              <Trash2 className="h-4 w-4" />
              Wipe All Data
            </Button>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showWipeDialog} onOpenChange={(open) => { setShowWipeDialog(open); if (!open) setConfirmText(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Wipe All Data</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all sessions, all players, and all data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <p className="text-sm font-medium">Type <span className="font-mono font-bold">DELETE</span> to confirm:</p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="font-mono"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={wiping}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== 'DELETE' || wiping}
              onClick={(e) => { e.preventDefault(); handleWipe(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {wiping ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirm Wipe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground text-center">
          SSC Score v2.0.1 ·{" "}
          <a href="/changelog" className="underline underline-offset-2 hover:text-foreground">
            What's new
          </a>
        </p>
      </div>
    </div>
  );
}
